import Cookies from "js-cookie";
import React, { useEffect, useRef, useState } from "react";
import { getDateFormat } from "../../Apis/functions";
import {colors} from "../../enums/colors";
import moment from "moment";
import {CustomDateRangePickerStyle} from "../Styles";
import {calculateRange} from "./Model";

/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx

 * Business logic for the `CustomDateRangePicker` component.
 * Handles state management for open/close, selected ranges,
 * responsive months display, and user interactions (apply/cancel).
 *
 * @param {Object} props - Hook props.
 * @param {Object} props.value - Initial date range with `start` and `end` values.
 * @param {Function} props.setRangeDate - Callback to update the parent state with the selected range.
 * @param {boolean} props.endDateIndeterminate - Indicates whether it will be for intervals where start is a date and end is a variable that represents an undefined value.
 * @param {string} props.textIndeterminate - Mask text for the end variable input: for example, "forever".
 * @param {number} props.rangeLength - Fixed amount of time to extend the range from the selected start date.
 * @param {string} props.rangeUnit - Unit of time for the range calculation. Used together with `rangeLength`
 *                                   to automatically determine the end date (e.g., 7 DAYS, 1 MONTH).
 **/
const CustomDateRangePickerApp = ({
    value,
    setRangeDate,
    endDateIndeterminate,
    textIndeterminate,
    rangeLength,
    rangeUnit
}) => {
    const lCode = Cookies.get("i18next") || "en";

    // Final confirmed date range
    const [range, setRange] = useState([
        {
            startDate: value?.start ? new Date(value.start) : new Date(),
            endDate: endDateIndeterminate ? ( value?.start ? new Date(value?.start) : new Date() )
                                          : ( value?.end ? new Date(value.end) : new Date() ),
            key: "selection",
        },
    ]);

    // Temporary date range while selecting
    const [tempRange, setTempRange] = useState([...range]);
    const calendarRef = useRef(null);
    const lastClickedDateRef = useRef(null);
    const [focusedRange, setFocusedRange] = useState([0, 0]);

    /**
     * This function validates cases where the start date has a value and the end date is an indefinite date.
     * If the endDateIndeterminate variable is true, then the endDate variable takes the same value as the start variable, because the component always expects two intervals.
     */
    useEffect(() => {
        if(value){
            const newRange = [
                {
                    startDate: value?.start ? new Date(value.start) : new Date(),
                    endDate: endDateIndeterminate ? ( value?.start ? new Date(value?.start) : new Date() )
                                                  : ( value?.end ? new Date(value.end) : new Date() ),
                    key: "selection",
                }
            ];

            setRange(newRange);
            setTempRange(newRange);
        }
    }, [value]);

    // Popper state
    const [open, setOpen] = useState(false);
    const anchorRef = useRef(null);

    // Responsive months display
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const [height, setHeight] = useState(0);
    const buttonHeight = 50;//Button height with name "Confirm"

    /**
     * Calculates and sets the optimal height for the DateDateRangePicker popper
     * based on the available viewport space above and below the input element.
     *
     * The function ensures:
     * - The popper is positioned either above or below, depending on available space.
     * - The content height is reduced only when necessary.
     * - The confirm button (fixed height) always remains visible.
     *
     * Logic:
     * 1. Measure the space above and below the trigger element, subtracting the
     *    height needed for the confirm button.
     * 2. If the full content height fits below, use it.
     * 3. If it fits above, use it.
     * 4. If it doesn't fit entirely, reduce the content height proportionally
     *    based on the side with the most available space.
     */
    const calculateTop = () => {
        if (!anchorRef.current) return;

        const rect = anchorRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - (rect.bottom + 30);
        const spaceAbove = rect.top - 20 ;

        const contentHeight = 372 + buttonHeight;

        let finalHeight;

        if (spaceBelow >= contentHeight) {
            finalHeight = contentHeight; // Fully fits below
        } else if (spaceAbove >= contentHeight) {
            finalHeight = contentHeight; // Fully fits above
        } else if (spaceBelow >= spaceAbove) {
            finalHeight = spaceBelow; // Reduce content below
        } else if (spaceAbove >= spaceBelow) {
            finalHeight = spaceAbove; // Reduce content above
        }
        setHeight(finalHeight);
    };


    useEffect(() => {
        if(open) calculateTop()
    }, [open]);

    useEffect(() => {
        const handleResize = () => setWindowWidth(window.innerWidth);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const monthsToShow = windowWidth < 600 ? 1 : 2;

    /** Open the calendar and initialize temp range */
    const onFocus = () => {
        setTempRange([...range]);
        setOpen(true);
    };

    /** Close calendar when clicking outside */
    const handleClickAway = event => {
        if (anchorRef.current && anchorRef.current.contains(event.target)) return;

        const hasChanges =
            tempRange[0]?.startDate?.getTime() !== range[0]?.startDate?.getTime() ||
            tempRange[0]?.endDate?.getTime() !== range[0]?.endDate?.getTime();

        if (hasChanges) {
            handleApply();
        } else {
            setOpen(false);
        }
    };

    /**
     * Handle changes in the date range picker.
     *
     * Updates the temporary range (`tempRange`) when the user selects a date.
     * It also manages special cases for indefinite end dates and keeps track
     * of the last clicked date to control calendar window focus.
     *
     * @param {Object} item - Object containing the selected range.
     * @param {Object} item.selection - The selection object from the date picker.
     * @param {Date} item.selection.startDate - Newly selected start date.
     * @param {Date} item.selection.endDate - Newly selected end date.
     *
     * Behavior:
     * - If `endDateIndeterminate` is true, sets `endDate` equal to `startDate`.
     * - Updates `lastClickedDateRef` depending on which side of the range (start or end) is focused.
     * - Updates `tempRange` with the newly selected dates while preserving unchanged dates.
     */
    const handleChange = item => {
        setTempRange(prev => {
            const prevRange = prev[0];
            const next = item.selection;

            if (endDateIndeterminate) {
                const startDate = next.startDate;
                return [{
                    ...prevRange,
                    startDate,
                    endDate: startDate,
                    key: 'selection'
                }];
            }

            if (focusedRange[1] === 0 && next.startDate) {
                lastClickedDateRef.current = next.startDate;
            }

            if (focusedRange[1] === 1 && next.endDate) {
                lastClickedDateRef.current = next.endDate;
            }

            let startDate = next.startDate ?? prevRange.startDate;
            let endDate = next.endDate ?? prevRange.endDate;

            if (
                rangeLength && rangeUnit &&
                focusedRange[1] === 0 &&
                next.startDate
            ) {
                endDate = calculateRange(next.startDate, rangeLength, rangeUnit);
            }


            return [{
                ...prevRange,
                startDate,
                endDate,
            }];
        });
    };

    /**
     * Repositions the visible calendar months to keep the last user-clicked date
     * within view after a range change.
     *
     * This effect runs whenever the temporary range or the number of visible months
     * changes. If the user has clicked a date (start or end), the calendar is
     * programmatically focused to ensure that date is visible in the correct
     * month window.
     *
     * Behavior:
     * - Uses the last clicked date (start or end) as the reference.
     * - When multiple months are displayed, shifts the focus so the clicked date
     *   appears in the appropriate month pane.
     * - Resets the last clicked reference after focusing to avoid unnecessary
     *   re-renders or unwanted jumps.
     *
     * Dependencies:
     * - tempRange: triggers repositioning when the user updates the selection.
     * - monthsToShow: recalculates the focus when responsive layout changes.
     */
    useEffect(() => {
        if(!endDateIndeterminate) return;

        if ( lastClickedDateRef.current && calendarRef.current?.focusToDate) {
            const focusDate = getFocusDateForMonths(
                lastClickedDateRef.current,
                monthsToShow
            );
            calendarRef.current.focusToDate(focusDate);
        }

        lastClickedDateRef.current = null;
    }, [tempRange, monthsToShow]);

    /**
     * Calculates the calendar focus date based on the number of visible months.
     *
     * When multiple months are displayed, the calendar needs to be shifted so that
     * the target date appears in the correct visible panel (usually the rightmost one).
     *
     * Example:
     * - If 2 months are visible and the clicked date is June,
     *   the calendar will focus on May so June appears as the second month.
     *
     * @param {Date} date - The date that should remain visible in the calendar.
     * @param {number} monthsToShow - Number of months currently rendered.
     * @returns {Date|null} A date used to reposition the calendar, or null if invalid.
     */
    const getFocusDateForMonths = (date, monthsToShow) => {
        if (!date) return null;
        if (monthsToShow <= 1) return date;

        const d = new Date(date);
        d.setMonth(d.getMonth() - (monthsToShow - 1));
        return d;
    };

    /** Confirm selection and propagate to parent */
    const handleApply = () => {
        setRange([...tempRange]);
        if (setRangeDate) {
            setRangeDate((prev) => ({
                ...prev,
                start: tempRange[0].startDate,
                end: tempRange[0].endDate,
            }));
        }
        setOpen(false);
    };

    /** Cancel selection and reset tempRange */
    const handleCancel = () => {
        setTempRange([...range]);
        setOpen(false);
    };

    /** Formatted display value */
    const formattedValue =
        tempRange[0].startDate && tempRange[0].endDate
            ? `${getDateFormat(tempRange[0].startDate, false)} \u2192 ${getDateFormat(tempRange[0].endDate, false)}`
            : "";
    const start = tempRange?.[0]?.startDate;
    const displayedValue = endDateIndeterminate && start
        ? `${getDateFormat(start, false)} - ${textIndeterminate}`
        : formattedValue;

    /**
     * Custom renderer for individual day cells inside the date range picker.
     * It determines the background and text color based on whether the date
     * is the start, end, or within the selected date range.
     *
     * @param {Date} date - The current date cell being rendered.
     * @returns {JSX.Element} A styled `<div>` containing the day number.
     */
    const getStyleDayContentRenderer = (date) => {
        const start = tempRange[0].startDate;
        const end = tempRange[0].endDate;
        let bgColor = colors.transparent;
        let color = colors.general950;

        if (
            moment(date).isSame(start, "day") ||
            moment(date).isSame(end, "day")
        ) {
            bgColor = colors.primary800;
            color = colors.general00;
        } else if (
            moment(date).isAfter(start, "day") &&
            moment(date).isBefore(end, "day")
        ) {
            bgColor = colors.text50;
            color = colors.general950;
        }

        return (
            <div className="d-flex align-items-center justify-content-center" style={CustomDateRangePickerStyle.dayContainer(bgColor, color)}>
                {date.getDate()}
            </div>
        );
    }

    return {
        open,
        lCode,
        anchorRef,
        formattedValue: displayedValue,
        monthsToShow,
        tempRange,
        height,
        buttonHeight,
        calendarRef,
        focusedRange,
        setFocusedRange,
        handleCancel,
        handleApply,
        handleChange,
        handleClickAway,
        onFocus,
        getStyleDayContentRenderer
    };
};

export default CustomDateRangePickerApp;
